// Bewaarde analyses (Data & Insight, DI-007).
//
// Dezelfde vorm als de andere handgeschreven clients hier: de dataProvider kent alleen resources met
// een lijst/detail-vorm, en dit zijn drie losse aanroepen op één pad.
//
// Het openen gaat bewust via het opgeslagen plan en niet door de vraag opnieuw te stellen. Zou het
// scherm de vraag opnieuw laten vertalen, dan verandert de analyse zodra de vertaalregels
// veranderen — en dan geeft dezelfde bewaarde analyse stilletjes andere cijfers onder dezelfde naam.
// Precies wat de opgeslagen definitie moet voorkomen.
import { getAccessToken } from "../auth";
import { clearAccessToken } from "../auth/tokenStore";
import { restoreSession } from "../authProvider";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

function headers(): Record<string, string> {
  const h: Record<string, string> = { "Content-Type": "application/json" };
  const token = getAccessToken();
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

/**
 * De schermstand van een bewaarde standaardanalyse: welk onderdeel, welke filters.
 *
 * <p>Leeg bij een getypte vraag. Het plan op de server bepaalt het antwoord; dit bepaalt wat de
 * filterbalk laat zien na het openen — zonder dat tweede staat er een cijfer met filters ernaast
 * die er niet bij horen.
 */
export interface AnalyseStand {
  onderdeel: string;
  vanJaar: number | null;
  totJaar: number | null;
  /** Het bereik op de dag af; gaat vóór de jaartallen als het erin staat. */
  vanDatum: string | null;
  totDatum: string | null;
  diersoorten: string[] | null;
  uitsplitsing: string | null;
  eenheid: string | null;
  /** Een gemeente, of een woonplaats met "wp:" ervoor. */
  gemeente: string | null;
  ouderDan: number | null;
  ouderDanEenheid: string | null;
  jongerDan: number | null;
  jongerDanEenheid: string | null;
  vestigingen: string[] | null;
}

export interface BewaardeAnalyse {
  id: string;
  naam: string;
  omschrijving: string | null;
  vraag: string;
  zichtbaarheid: string;
  vanMij: boolean;
  stand: AnalyseStand | null;
}

/**
 * fetch met dezelfde 401-afhandeling als de dataProvider: het token stil vernieuwen uit het
 * refresh-cookie en één keer opnieuw proberen. Lukt dat niet, dan is de sessie echt weg en gaat het
 * scherm naar de inlogpagina in plaats van een kale "HTTP 401" te tonen. Na het inloggen komt de
 * gebruiker via de URL-stand weer op hetzelfde scherm.
 */
async function insightFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, init);
  if (res.status !== 401) return res;
  if (await restoreSession()) {
    const opnieuw = new Headers(init.headers);
    const token = getAccessToken();
    if (token) opnieuw.set("Authorization", `Bearer ${token}`);
    const tweede = await fetch(url, { ...init, headers: opnieuw });
    if (tweede.status !== 401) return tweede;
  }
  clearAccessToken();
  globalThis.location?.reload();
  return res;
}

export class InsightError extends Error {
  constructor(readonly status: number, readonly serverMelding?: string) {
    super(serverMelding || `HTTP ${status}`);
    this.name = "InsightError";
  }
}

async function melding(res: Response): Promise<string | undefined> {
  try {
    const body = (await res.json()) as { message?: string };
    return body.message;
  } catch {
    return undefined;
  }
}

export async function lijstAnalyses(): Promise<BewaardeAnalyse[]> {
  const res = await insightFetch(`${API_URL}/insights`, { headers: headers() });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
  return (await res.json()) as BewaardeAnalyse[];
}

export async function bewaarAnalyse(invoer: {
  naam: string;
  omschrijving?: string;
  vraag: string;
  zichtbaarheid: string;
  /**
   * De schermstand bij een standaardanalyse.
   *
   * <p>Staat die erin, dan bouwt de server het plan uit zijn eigen catalogus en gaat de vraag niet
   * langs het taalmodel. Het scherm stuurt dus nooit een plan mee: dat zou invoer zijn die
   * rechtstreeks een definitie wordt.
   */
  stand?: AnalyseStand;
}): Promise<string> {
  const res = await insightFetch(`${API_URL}/insights`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(invoer),
  });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
  return ((await res.json()) as { id: string }).id;
}

/**
 * Voert de opgeslagen definitie opnieuw uit op de actuele gegevens.
 *
 * <p>Het antwoord heeft dezelfde vorm als /search/execute, zodat het scherm er niets anders mee hoeft
 * te doen dan met een gewone vraag.
 */
export async function voerAnalyseUit(id: string): Promise<unknown> {
  const res = await insightFetch(`${API_URL}/insights/${encodeURIComponent(id)}/uitvoeren`, {
    headers: headers(),
  });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
  return await res.json();
}

export async function verwijderAnalyse(id: string): Promise<void> {
  const res = await insightFetch(`${API_URL}/insights/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: headers(),
  });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
}

/**
 * Haalt de analyse als Excel-bestand op en biedt hem aan.
 *
 * <p>Het bestand wordt op de server gemaakt en niet in de browser. Dat is geen willekeurige keuze:
 * de browser heeft alleen wat er op het scherm staat, en dat is bij een lijst de eerste pagina. Een
 * export die stilzwijgend bij record 50 ophoudt is erger dan geen export — je ziet aan het bestand
 * niet dat er iets mist.
 *
 * <p>De naam van het bestand komt uit de Content-Disposition van de server, zodat de naamgeving op
 * één plek staat en niet op twee uiteenlopende.
 */
export async function exporteerExcel(invoer: {
  vraag: string;
  /** De aangeklikte groep, als er is doorgeklikt; dan komt die lijst in het bestand. */
  labels?: string[];
  /** De weergave op het scherm (staaf, gestapeld, lijn, tabel); het bestand tekent dezelfde grafiek. */
  grafiek?: string;
  /** Of het scherm een oplopend totaal toonde. */
  cumulatief?: boolean;
}): Promise<void> {
  const h = headers();
  const res = await insightFetch(`${API_URL}/insights/export`, {
    method: "POST",
    headers: h,
    body: JSON.stringify(invoer),
  });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
  await biedAan(res);
}

/** Een bewaarde analyse als Excel; opnieuw uitgerekend, net als bij openen. */
export async function exporteerBewaardeAnalyse(id: string): Promise<void> {
  const res = await insightFetch(`${API_URL}/insights/${encodeURIComponent(id)}/export`, {
    headers: headers(),
  });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
  await biedAan(res);
}

async function biedAan(res: Response): Promise<void> {
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = bestandsnaamUit(res.headers.get("Content-Disposition"))
    ?? `data-insight-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Niet meteen intrekken: sommige browsers zijn nog met de download bezig als de klik terugkeert.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * De bestandsnaam uit een Content-Disposition.
 *
 * <p>Eerst `filename*` (RFC 5987) en dan pas `filename`: bij een naam met een accent zet de server
 * daar de bruikbare versie neer en in `filename` een uitgeklede. Andersom lezen levert een bestand
 * met vraagtekens in de naam.
 */
export function bestandsnaamUit(kop: string | null): string | undefined {
  if (!kop) return undefined;
  const ster = /filename\*=UTF-8''([^;]+)/i.exec(kop);
  if (ster) {
    try {
      return decodeURIComponent(ster[1]);
    } catch {
      // Een kapotte codering is geen reden om de download te laten mislukken; val terug op filename.
    }
  }
  const gewoon = /filename="?([^";]+)"?/i.exec(kop);
  return gewoon ? gewoon[1] : undefined;
}

/** De keuzelijst met vaste onderdelen, elk met zijn definitie. */
export async function lijstOnderdelen(): Promise<unknown> {
  const res = await insightFetch(`${API_URL}/insights/onderdelen`, { headers: headers() });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
  return await res.json();
}

/**
 * Voert een vast onderdeel uit met de gekozen filters.
 *
 * <p>Het antwoord heeft dezelfde vorm als een getypte vraag, zodat het scherm er niets anders mee
 * hoeft te doen. Onder water bouwt het onderdeel gewoon een plan en loopt het langs dezelfde
 * validator en vestigingsafscherming.
 */
/** De velden van de filterbalk die de server kent. */
export interface OnderdeelInvoer {
  onderdeel: string;
  vanJaar: number | null;
  totJaar: number | null;
  vanDatum?: string | null;
  totDatum?: string | null;
  diersoorten: string[];
  uitsplitsing: string;
  eenheid: "DAG" | "WEEK" | "MAAND" | "KWARTAAL" | "JAAR";
  gemeente: string;
  ouderDan?: number | null;
  ouderDanEenheid?: string | null;
  jongerDan?: number | null;
  jongerDanEenheid?: string | null;
  /** De aangevinkte vestigingen; leeg laat de huidige vestiging staan. */
  vestigingen?: string[];
}

/**
 * Het verzoek zoals de server het kent.
 *
 * <p>Met opzet niet het hele keuze-object doorsturen. "Cumulatief" hoort er bijvoorbeeld niet in:
 * dat is een manier om dezelfde cijfers te tonen en geen andere vraag, en het meesturen zou
 * betekenen dat het omzetten van die schakelaar opnieuw laat rekenen.
 */
export function verzoekVan(invoer: OnderdeelInvoer): Record<string, unknown> {
  return {
    onderdeel: invoer.onderdeel,
    vanJaar: invoer.vanJaar,
    totJaar: invoer.totJaar,
    vanDatum: invoer.vanDatum ?? null,
    totDatum: invoer.totDatum ?? null,
    diersoorten: invoer.diersoorten ?? [],
    uitsplitsing: invoer.uitsplitsing,
    eenheid: invoer.eenheid,
    gemeente: invoer.gemeente,
    ouderDan: invoer.ouderDan ?? null,
    ouderDanEenheid: invoer.ouderDanEenheid ?? null,
    jongerDan: invoer.jongerDan ?? null,
    jongerDanEenheid: invoer.jongerDanEenheid ?? null,
    vestigingen: invoer.vestigingen ?? [],
  };
}

export async function voerOnderdeelUit(invoer: OnderdeelInvoer): Promise<unknown> {
  const res = await insightFetch(`${API_URL}/insights/onderdeel`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(verzoekVan(invoer)),
  });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
  return await res.json();
}

/**
 * Stelt een vrije vraag en voert hem meteen uit.
 *
 * <p>Imperatief en niet via een hook: de vraag gaat pas naar de server als iemand hem stelt. Een
 * hook die op de ingetypte tekst reageert zou een zware analyse draaien over "hoev", "hoeve",
 * "hoevee" — allemaal zinloos en allemaal duur.
 *
 * @param groep de aangeklikte staaf, als er is doorgeklikt
 */
export async function voerVraagUit(vraag: string, groep?: string[]): Promise<unknown> {
  const res = await insightFetch(`${API_URL}/search/execute?q=${encodeURIComponent(vraag)}`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ query: vraag, groep }),
  });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
  return await res.json();
}

/** Het voorvoegsel waarmee het scherm zegt dat een keuze een woonplaats is en geen gemeente. */
export const WOONPLAATS_VOORVOEGSEL = "wp:";

/** Eén gemeente met de woonplaatsen die eronder vallen. */
export interface Gebied {
  naam: string;
  woonplaatsen: string[];
}

/**
 * De gemeenten om op te filteren, met hun woonplaatsen.
 *
 * <p>Dezelfde lijst die het inboeken gebruikt, en met opzet niet een eigen tweede lijst: die zou
 * ooit een gemeente missen, en dan geeft hetzelfde filter op twee schermen een ander antwoord.
 *
 * <p>Alleen de namen, want daar wordt op gefilterd — de analyse groepeert op de naam van de
 * gemeente en niet op zijn id, zodat er in de grafiek "Enschede" staat en geen 42.
 *
 * <p>De woonplaatsen komen mee omdat de gemeenten daarmee uitgebreid zijn: een dier wordt in
 * Losser gevonden en niet in "de gemeente Losser", en op de woonplaats filteren is preciezer dan
 * op de gemeente eromheen.
 */
export async function lijstGemeenten(): Promise<Gebied[]> {
  const res = await insightFetch(`${API_URL}/animals/reference/gemeenten`, { headers: headers() });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
  const lijst = (await res.json()) as { label?: string; woonplaatsen?: string[] }[];
  return lijst
    .map((g) => ({ naam: g.label ?? "", woonplaatsen: g.woonplaatsen ?? [] }))
    .filter((g) => g.naam !== "");
}

/**
 * De vestigingen om op te filteren.
 *
 * <p>Alleen de toegankelijke, en van de server: een lijst waarin je iets kunt aanvinken dat je
 * daarna niet te zien krijgt is een lijst die liegt. De server doorsnijdt bij het uitvoeren nog een
 * tweede keer — deze lijst bepaalt wat je kúnt kiezen, niet wat je mag zien.
 */
export async function lijstVestigingen(): Promise<string[]> {
  const res = await insightFetch(`${API_URL}/insights/vestigingen`, { headers: headers() });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
  return ((await res.json()) as { naam: string }[]).map((v) => v.naam);
}

/** Wat er in de vier kaarten boven het scherm staat. */
export interface Kerncijfer {
  sleutel: string;
  naam: string;
  eenheid: string;
  waarde: number | null;
  vorig: number | null;
  /** Het verschil in procenten; null als er niets te vergelijken valt. */
  procent: number | null;
  /** Welke kant het gunstige op ligt: HOGER_BETER, LAGER_BETER of GEEN. */
  richting: "HOGER_BETER" | "LAGER_BETER" | "GEEN";
  /** Waarom er geen vergelijking staat, als die er niet is. */
  toelichting: string | null;
}

/**
 * De vier kerncijfers over dezelfde filters als de analyse.
 *
 * <p>Eén verzoek en niet acht: de server rekent ze samen uit. Acht losse aanroepen zouden acht
 * regels in het auditlogboek zetten voor één keer kijken.
 */
export async function haalKerncijfers(invoer: OnderdeelInvoer): Promise<Kerncijfer[]> {
  const res = await insightFetch(`${API_URL}/insights/kerncijfers`, {
    method: "POST",
    headers: headers(),
    // Zonder onderdeel en zonder uitsplitsing: de server bepaalt wélke vier kaarten het zijn, en
    // een kaart toont één getal.
    body: JSON.stringify({ ...verzoekVan(invoer), onderdeel: "", uitsplitsing: "" }),
  });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
  return (await res.json()) as Kerncijfer[];
}

/** Eén regel in de tabel met recente opnames. */
export interface RecenteOpname {
  registratienummer: string | null;
  diersoort: string | null;
  ras: string | null;
  geslacht: string | null;
  /** De leeftijd op de dag van opname, in jaren; null zonder geboortedatum. */
  leeftijd: number | null;
  herkomst: string | null;
  datum: string | null;
}

/**
 * De laatste opnames.
 *
 * <p>Een eigen leesactie en niet de lijstquery: daar is een dier de rij, en hier een opname. Een
 * dier met drie verblijven hoort hier drie keer te staan.
 */
export async function haalRecenteOpnames(
  vestigingen: string[] = [], aantal = 5,
): Promise<RecenteOpname[]> {
  const p = new URLSearchParams({ aantal: String(aantal) });
  for (const v of vestigingen) p.append("vestigingen", v);
  const res = await insightFetch(`${API_URL}/insights/recente-opnames?${p}`, { headers: headers() });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
  return (await res.json()) as RecenteOpname[];
}

/** Hoe fijn de herkomstkaart is: per gemeente, of per viercijferig postcodegebied. */
export type HerkomstNiveau = "GEMEENTE" | "POSTCODE";

/** Wat de kaart toont: waar de dieren vandaan komen, of waar ze naartoe gaan. Nooit samen. */
export type HerkomstRichting = "OPNAME" | "VERTREK";

export interface HerkomstVerzoek {
  vanDatum: string | null;
  totDatum: string | null;
  diersoorten: string[];
  richting: HerkomstRichting;
  /** Soorten opname of vertrek, afhankelijk van de richting; leeg is alle. */
  soorten: string[];
  /** Leeg is de vestiging waar je in zit, net als bij de analyses. */
  vestigingen: string[];
  niveau: HerkomstNiveau;
}

/** Eén plek op de kaart: een gemeente of een postcodegebied, met het aantal opnames. */
export interface HerkomstPlek {
  naam: string;
  lat: number;
  lon: number;
  aantal: number;
}

export interface Herkomst {
  richting: HerkomstRichting;
  niveau: HerkomstNiveau;
  plekken: HerkomstPlek[];
  totaal: number;
  /** Opnames zonder gemeente (of postcode): die staan niet op de kaart. */
  zonderLocatie: number;
  /** Opnames uit een plaats die niet op de kaart te vinden is. */
  nietGevonden: number;
  /** Plekken die de server nog opzoekt; het scherm vraagt dan even later opnieuw. */
  nogOpzoeken: number;
}

export async function haalHerkomst(verzoek: HerkomstVerzoek): Promise<Herkomst> {
  const res = await insightFetch(`${API_URL}/insights/herkomst`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(verzoek),
  });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
  return (await res.json()) as Herkomst;
}

/** De kaart als Excel: de plekken met hun aantal, de opnames of vertrekken erachter, en de filters. */
export async function exporteerHerkomst(verzoek: HerkomstVerzoek): Promise<void> {
  const res = await insightFetch(`${API_URL}/insights/herkomst/export`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(verzoek),
  });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
  await biedAan(res);
}

/** De soorten opname of vertrek om de kaart op te filteren. */
export async function lijstSoorten(richting: HerkomstRichting): Promise<string[]> {
  const res = await insightFetch(`${API_URL}/insights/herkomst/soorten?richting=${richting}`, { headers: headers() });
  if (!res.ok) throw new InsightError(res.status, await melding(res));
  return (await res.json()) as string[];
}
